import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { AccountingReportsService } from '../../accounting-reports.service';
import { CrmClient } from '../../../crm/crm.model';
import { CrmService } from '../../../crm/crm.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { closeFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

interface StatementRow {
  partnerId: number | null;
  partnerType: string;
  partnerName: string;
  currencyCode: string;
  openingBalance: number;
  transactionDate: string | null;
  documentNumber: string | null;
  documentType: string | null;
  reference: string | null;
  debit: number | null;
  credit: number | null;
  runningBalance: number;
}

interface StatementGroup {
  name: string;
  currencyCode: string;
  opening: number;
  closing: number;
  debits: number;
  credits: number;
  rows: StatementRow[];
}

@Component({
  selector: 'app-standard-report',
  standalone: true,
  templateUrl: './standard-report.component.html',
  styleUrls: ['./standard-report.component.css'],
  imports: [
    CommonModule, FormsModule, ButtonModule, CardModule, DatePickerModule, IftaLabelModule,
    ProgressBarModule, SelectModule, TableModule, ToastModule, PageShellComponent,
    PageHeroComponent, PageFormSectionComponent, PageDataPanelComponent, ReportEmailComponent
  ],
  providers: [AccountingReportsService, MessageService, CurrencyPipe, DatePipe, DecimalPipe]
})
export class StandardReportComponent implements OnInit {
  readonly reportActions = new PendingActionTracker<string>();
  reportCode = '';
  title = '';
  subtitle = '';
  startDate = new Date(new Date().getFullYear(), 0, 1);
  endDate = new Date();
  clients: CrmClient[] = [];
  selectedClient?: CrmClient | null;
  rows: Record<string, unknown>[] = [];
  summary: Record<string, unknown> = {};
  statementGroups: StatementGroup[] = [];
  emailRows: Record<string, unknown>[] = [];
  loading = false;
  private requestNumber = 0;

  constructor(
    route: ActivatedRoute,
    private reports: AccountingReportsService,
    private crm: CrmService,
    private session: SessionStorageService,
    private messages: MessageService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe
  ) {
    this.reportCode = route.snapshot.data['reportCode'] || '';
    this.title = route.snapshot.data['title'] || 'Accounting Report';
    this.subtitle = route.snapshot.data['subtitle'] || 'Review posted accounting activity.';
  }

  ngOnInit(): void {
    if (this.showPartnerFilter) {
      this.crm.getClients(this.companyId()).subscribe({
        next: clients => this.clients = clients ?? [],
        error: () => this.clients = []
      });
    }
    this.load();
  }

  get columns(): string[] {
    return this.rows.length ? Object.keys(this.rows[0]).filter(key => !/(?:^id$|Id$|ID$|_id$)/.test(key) && key !== 'partnerType') : [];
  }

  get customerStatements(): boolean {
    return this.reportCode === 'customer-statements';
  }

  get partnerStatements(): boolean {
    return this.customerStatements || this.reportCode === 'supplier-statements';
  }

  get balanceSections(): boolean {
    return this.partnerStatements || this.reportCode === 'bank-cash';
  }

  get transactionCount(): number {
    return this.statementGroups.reduce((count, group) => count + group.rows.length, 0);
  }

  get showPartnerFilter(): boolean {
    return ['customer-statements', 'supplier-statements', 'ar-register', 'ap-register'].includes(this.reportCode);
  }

  get partnerOptions(): CrmClient[] {
    if (this.customerStatements || this.reportCode === 'ar-register') {
      return this.clients.filter(client =>
        (!client.roles?.length || client.roles.includes('CUSTOMER')) &&
        (client.linkedCompanyId != null || client.linkedCustomerId != null));
    }
    if (this.reportCode === 'supplier-statements' || this.reportCode === 'ap-register') {
      return this.clients.filter(client =>
        (!client.roles?.length || client.roles.some(role => role !== 'CUSTOMER')) &&
        (client.linkedCompanyId != null || client.linkedCustomerId != null)
      );
    }
    return this.clients;
  }

  get metricReport(): boolean {
    return ['cash-flow', 'executive-summary'].includes(this.reportCode);
  }

  get summaryEntries(): { key: string; value: unknown }[] {
    return Object.entries(this.summary).map(([key, value]) => ({ key, value }));
  }

  get metricNameField(): string {
    return this.reportCode === 'cash-flow' ? 'section' : 'metric';
  }

  get metricValueField(): string {
    return this.reportCode === 'cash-flow' ? 'netCashFlow' : 'value';
  }

  load(): void {
    const requestNumber = ++this.requestNumber;
    if (!this.validPeriod()) {
      this.rows = [];
      this.statementGroups = [];
      this.emailRows = [];
      this.loading = false;
      this.messages.add({ severity: 'warn', summary: 'Invalid period', detail: 'Start Date must be on or before End Date.' });
      return;
    }
    this.loading = true;
    this.rows = [];
    this.statementGroups = [];
    this.emailRows = [];
    this.reports.getStandardReport(this.reportCode, this.companyId(), this.reportFilter()).subscribe({
      next: response => {
        if (requestNumber !== this.requestNumber) return;
        this.rows = response.rows ?? [];
        this.summary = response.summary ?? {};
        this.title = response.title || this.title;
        if (this.balanceSections) this.groupStatements();
        else this.emailRows = this.visibleRows();
        this.loading = false;
      },
      error: error => {
        if (requestNumber !== this.requestNumber) return;
        console.error(`Failed to load ${this.title}`, error);
        this.rows = [];
        this.loading = false;
        this.messages.add({ severity: 'error', summary: 'Error', detail: `Failed to load ${this.title}.` });
      }
    });
  }

  exportExcel(): void {
    if (this.loading || !this.rows.length) return;
    const sheet = XLSX.utils.aoa_to_sheet(this.balanceSections ? this.statementExcelRows() : this.standardExcelRows());
    sheet['!cols'] = (this.balanceSections ? [18, 28, 17, 45, 18, 18, 20]
      : this.columns.map(column => Math.max(18, this.heading(column).length + 4))).map(wch => ({ wch }));
    const workbook = { Sheets: { Report: sheet }, SheetNames: ['Report'] };
    const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })]);
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${this.reportCode}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  print(): void {
    if (this.loading || !this.validPeriod()) return;
    if (!this.reportActions.begin('print')) return;
    const target = window.open('', '_blank');
    this.reports.printFinanceReport(this.reportCode, this.companyId(), this.reportFilter())
      .pipe(finalize(() => this.reportActions.end('print'))).subscribe({
      next: blob => openFinancialReportPdf(blob, target),
      error: () => {
        closeFinancialReportPdf(target);
        this.messages.add({ severity: 'error', summary: 'Print unavailable', detail: `Failed to generate the ${this.title} PDF.` });
      }
    });
  }

  downloadPdf(): void {
    if (this.loading || !this.validPeriod() || !this.reportActions.begin('download')) return;
    this.reports.printFinanceReport(this.reportCode, this.companyId(), this.reportFilter())
      .pipe(finalize(() => this.reportActions.end('download'))).subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.reportCode}_${this.dateOnly(this.startDate)}_${this.dateOnly(this.endDate)}.pdf`;
          link.click();
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        },
        error: () => this.messages.add({ severity: 'error', summary: 'PDF unavailable', detail: `Failed to download ${this.title}.` })
      });
  }

  private groupStatements(): void {
    const groups = new Map<string, StatementGroup>();
    for (const source of this.rows) {
      const row = this.reportCode === 'bank-cash' ? {
        ...source, partnerId: source['accountId'], partnerType: 'ACCOUNT',
        partnerName: `${source['accountCode']} · ${source['accountName']}`,
        documentNumber: source['entryNumber'], documentType: source['reconciliationStatus'],
        reference: source['description']
      } as unknown as StatementRow : source as unknown as StatementRow;
      const key = `${row.partnerType}:${row.partnerId ?? 'unassigned'}:${row.currencyCode}`;
      let group = groups.get(key);
      if (!group) {
        group = { name: row.partnerName, currencyCode: row.currencyCode,
          opening: Number(row.openingBalance), closing: Number(row.openingBalance), debits: 0, credits: 0, rows: [] };
        groups.set(key, group);
      }
      group.closing = Number(row.runningBalance);
      if (row.transactionDate) {
        group.rows.push(row);
        group.debits += Math.round(Number(row.debit ?? 0) * 100);
        group.credits += Math.round(Number(row.credit ?? 0) * 100);
      }
    }
    this.statementGroups = [...groups.values()].map(group => ({
      ...group, debits: group.debits / 100, credits: group.credits / 100
    }));
    this.emailRows = this.statementGroups.flatMap(group => (group.rows.length ? group.rows : [null]).map(row => ({
      [this.reportCode === 'bank-cash' ? 'Account' : this.customerStatements ? 'Customer' : 'Supplier']: group.name,
      Currency: group.currencyCode, 'Opening Balance': group.opening,
      Date: row?.transactionDate ? this.datePipe.transform(row.transactionDate, 'yyyy-MM-dd') ?? '' : '',
      Document: row?.documentNumber ?? '',
      [this.reportCode === 'bank-cash' ? 'Status' : 'Type']: row?.documentType ?? '',
      [this.reportCode === 'bank-cash' ? 'Description' : 'Reference']: row?.reference ?? '',
      Debit: row?.debit ?? 0, Credit: row?.credit ?? 0,
      'Running Balance': row?.runningBalance ?? group.opening, 'Closing Balance': group.closing
    })));
  }

  private statementExcelRows(): (string | number)[][] {
    const lines: (string | number)[][] = [
      [this.title], ['Period', `${this.dateOnly(this.startDate)} to ${this.dateOnly(this.endDate)}`],
      [this.reportCode === 'bank-cash' ? 'Account' : this.customerStatements ? 'Customer' : 'Supplier',
        this.reportCode === 'bank-cash' ? 'All cash and bank accounts' : this.selectedClient?.companyName ?? (this.customerStatements ? 'All customers' : 'All suppliers')], []
    ];
    for (const group of this.statementGroups) {
      lines.push([group.name, group.currencyCode], ['Opening Balance', group.opening],
        ['Date', 'Document', this.reportCode === 'bank-cash' ? 'Status' : 'Type',
          this.reportCode === 'bank-cash' ? 'Description' : 'Reference', 'Debit', 'Credit', 'Running Balance']);
      group.rows.forEach(row => lines.push([
        row.transactionDate ? this.datePipe.transform(row.transactionDate, 'yyyy-MM-dd') ?? '' : '',
        row.documentNumber ?? '', row.documentType ?? '',
        row.reference ?? '', Number(row.debit ?? 0), Number(row.credit ?? 0), Number(row.runningBalance)
      ]));
      lines.push(['Period Debits', group.debits], ['Period Credits', group.credits],
        ['Closing Balance', group.closing], []);
    }
    return lines;
  }

  private standardExcelRows(): (string | number)[][] {
    return [
      [this.title], ['Period', `${this.dateOnly(this.startDate)} to ${this.dateOnly(this.endDate)}`],
      ...this.summaryEntries.map(item => [this.heading(item.key),
        typeof item.value === 'number' ? item.value : String(item.value ?? '')]), [],
      this.columns.map(column => this.heading(column)),
      ...this.rows.map(row => this.columns.map(column =>
        typeof row[column] === 'number' ? row[column] as number : String(row[column] ?? '')))
    ];
  }

  private reportFilter(): { startDate: Date | string; endDate: Date | string; partnerId: number | null; partnerType: string | null } {
    return {
      startDate: this.dateOnly(this.startDate),
      endDate: this.dateOnly(this.endDate),
      partnerId: this.partnerId(),
      partnerType: this.partnerType()
    };
  }

  validPeriod(): boolean {
    return !!this.startDate && !!this.endDate && this.dateOnly(this.startDate) <= this.dateOnly(this.endDate);
  }

  private dateOnly(value: Date): string {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }

  private partnerType(): string | null {
    if (!this.selectedClient) return null;
    return this.selectedClient.linkedCompanyId != null ? 'COMPANY' : 'CUSTOMER';
  }

  heading(column: string): string {
    return column.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, value => value.toUpperCase());
  }

  private visibleRows(): Record<string, unknown>[] {
    return this.rows.map(row => Object.fromEntries(this.columns.map(column => [this.heading(column), row[column]])));
  }

  display(row: Record<string, unknown>, column: string): string {
    const value = row[column];
    if (value == null) return '-';
    if (/date/i.test(column)) return this.datePipe.transform(value as string | number | Date, 'dd-MMM-yyyy') || '-';
    if (/amount|balance|flow|cost|value|depreciation|debit|credit|profit|revenue|expense/i.test(column)) {
      const currencyCode = String(row['currencyCode'] || 'SAR');
      return /^[A-Z]{3}$/.test(currencyCode)
        ? this.currencyPipe.transform(Number(value), currencyCode, 'symbol', '1.2-2') || String(value)
        : `${currencyCode} ${this.decimalPipe.transform(Number(value), '1.2-2')}`;
    }
    if (typeof value === 'number' || value instanceof Number) {
      return this.decimalPipe.transform(Number(value), '1.0-4') || String(value);
    }
    return String(value);
  }

  private partnerId(): number | null {
    if (!this.selectedClient) return null;
    return this.selectedClient.linkedCompanyId ?? this.selectedClient.linkedCustomerId ?? null;
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }
}
